import { useApiClient } from '@skylabs-digital/react-proto-kit';
import { useMemo } from 'react';
import {
  deliverySchema,
  type Webhook,
  type WebhookDelivery,
  type WebhooksOverview,
  type WebhooksSeries,
  webhookSchema,
  webhooksOverviewSchema,
  webhooksSeriesSchema,
} from '../schemas/webhook.schema';
import type { FilesPage } from './files.api';
import { buildProcessingQuery, type ProcessingQuery } from './processing.api';
import { unwrap } from './unwrap';

export interface DeliveriesPage {
  data: WebhookDelivery[];
  meta: FilesPage['meta'];
}

export function useWebhookActions() {
  const { connector } = useApiClient();
  // Memoizado sobre el connector: los efectos de la card cuelgan de estos métodos.
  return useMemo(
    () => ({
      list: async (appId: string): Promise<Webhook[]> =>
        unwrap(await connector.get<unknown[]>(`apps/${appId}/webhooks`)).map((w) =>
          webhookSchema.parse(w),
        ),
      overview: async (q: ProcessingQuery): Promise<WebhooksOverview> =>
        webhooksOverviewSchema.parse(
          unwrap(await connector.get<unknown>(`webhooks/overview?${buildProcessingQuery(q)}`)),
        ),
      series: async (q: ProcessingQuery): Promise<WebhooksSeries> =>
        webhooksSeriesSchema.parse(
          unwrap(await connector.get<unknown>(`webhooks/series?${buildProcessingQuery(q)}`)),
        ),
      /** Sin `appId` es el listado global de la sección Webhooks. */
      deliveries: async (q: {
        appId?: string;
        endpointId?: string;
        eventType?: string;
        page: number;
        limit: number;
        status?: 'DELIVERED' | 'FAILED';
      }): Promise<DeliveriesPage> => {
        const params = new URLSearchParams({ page: String(q.page), limit: String(q.limit) });
        if (q.appId) params.set('appId', q.appId);
        if (q.endpointId) params.set('endpointId', q.endpointId);
        if (q.eventType) params.set('eventType', q.eventType);
        if (q.status) params.set('status', q.status);
        const res = await connector.get<unknown[]>(`webhooks/deliveries?${params}`);
        const data = unwrap(res).map((d) => deliverySchema.parse(d));
        const meta = (res as { meta?: FilesPage['meta'] }).meta ?? {
          total: data.length,
          page: q.page,
          limit: q.limit,
          totalPages: 1,
          hasNext: false,
          hasPrev: q.page > 1,
        };
        return { data, meta };
      },
      redeliver: async (appId: string, deliveryId: string): Promise<void> => {
        unwrap(
          await connector.post<unknown>(`webhooks/deliveries/${deliveryId}/redeliver`, { appId }),
        );
      },
    }),
    [connector],
  );
}
